import { Step as ModelStep } from "@redotech/redo-model/return-flow";
import { TriggerType } from "@redotech/redo-model/return-flow/trigger";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { produce } from "immer";
import * as React from "react";
import {
  StepDownstream,
  StepId,
  StepType,
  StepTypeDetailsProps,
} from "../step";

export interface TriggerState {
  triggerType: TriggerType;
  next: StepId | undefined;
  customTitle?: string;
}

export function triggerTypeLabel(type: TriggerType | string) {
  return type.charAt(0).toUpperCase() + type.slice(1).replaceAll("_", " ");
}

export const TRIGGER = (
  Details: React.ComponentType<StepTypeDetailsProps<TriggerState>>,
): StepType<TriggerState, ModelStep.Trigger> => ({
  Details: (props: StepTypeDetailsProps<TriggerState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return triggerTypeLabel(state.triggerType);
  },
  displayTitle(state) {
    return triggerTypeLabel(state.triggerType);
  },
  valid(state) {
    return !!state.next && !!state.triggerType;
  },
  title: "Trigger",
  Icon: () => null,
  description() {
    return "";
  },
  start(state) {
    return state.next;
  },
  empty: { triggerType: TriggerType.ORDER_CREATED, next: undefined },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(`Invalid handle index ${handleIndex} for Trigger step`);
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.COMPACT;
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.TRIGGER,
      triggerType: state.triggerType,
      next: id(state.next!),
    };
  },
  fromModel(model, id) {
    return {
      customTitle: model.customTitle,
      triggerType: model.triggerType,
      next: id(model.next),
    };
  },
});
